import { useCallback, useState } from "react";
import type {
  FieldArrayWithId,
  FieldErrors,
  UseFieldArrayRemove,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import {
  AlertCircle,
  FileCheck2,
  MapPin,
  PackagePlus,
  Scale,
  Signpost,
  Trash2,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { FormField, Input, Select, Textarea } from "../UI/FormField";
import { AddressAutocompleteField } from "../GooglePlaces/AddressAutocompleteField";
import { CUSTOM_STOP_KIND_VALUES } from "../../constants/containerOrderConstants";
import type { Country } from "../../types";
import type {
  ContainerOrderFormData,
  ContainerStopKind,
} from "../../types/containerOrders";
import { MAX_ROUTE_STOPS } from "../../types/containerOrders";

/** The icon each kind wears, so a route of six is scannable rather than read. */
const KIND_ICON: Record<ContainerStopKind, typeof Signpost> = {
  customs: FileCheck2,
  weighing: Scale,
  extraLoading: PackagePlus,
  waypoint: Signpost,
  other: Signpost,
};

/** Where in the route a stop can be anchored, in route order. */
const GAPS = [
  "start",
  "terminalPickup",
  "loadingSite",
  "terminalDropoff",
] as const;

/** Three anchors plus the additions — the same ceiling a container is held to. */
export const MAX_ADDITIONAL = MAX_ROUTE_STOPS - 3;

/**
 * A blank added stop.
 *
 * Every field is defaulted rather than left undefined: each is registered as a
 * controlled input, and an undefined turns into an uncontrolled one on the
 * first keystroke. Lives here, beside the card that renders it, even though the
 * "Add stop" button now sits in the section header — the two must not drift.
 */
export const makeAdditionalStop = (sequence: number) => ({
  after: "loadingSite" as const,
  sequence,
  kind: "other" as const,
  label: "",
  comment: "",
  addressId: null,
  partnerId: null,
  companyName: "",
  address: {
    street: "",
    city: "",
    state: "",
    postalCode: "",
    country: "",
    coordinates: undefined,
  },
  ramp: "",
  contactName: "",
  contactPhone: "",
  contactEmail: "",
  bookingRef: "",
  pin: "",
  date: "",
  time: "",
  enabled: true,
  skipReason: "",
});

interface OrderAdditionalStopsProps {
  /** The field array, owned by the parent so the header can hold the button. */
  fields: FieldArrayWithId<ContainerOrderFormData, "route.additionalStops">[];
  remove: UseFieldArrayRemove;
  register: UseFormRegister<ContainerOrderFormData>;
  setValue: UseFormSetValue<ContainerOrderFormData>;
  watch: UseFormWatch<ContainerOrderFormData>;
  errors: FieldErrors<ContainerOrderFormData>;
  readonly: boolean;
  getErrorMessage: (error: any) => string | undefined;
  countries: Country[];
  apiKey: string;
}

/**
 * The stops an order adds between the three it always has.
 *
 * The terminal → client → terminal route covers the overwhelming majority of
 * container moves, which is why the form is built around it. What it does not
 * cover is the tail, and the tail is not rare: a customs office between the
 * terminal and the client, a certified weighbridge the VGM has to be read off, a
 * second site the cargo is collected from, a rest the tachograph mandates. Each
 * is a place a truck stops, waits, and is expected to account for — and until
 * now each had to be smuggled into a notes field, where no driver was ever shown
 * it and no report could ever count it.
 *
 * Set here, on the order, rather than only on the container: a batch of twenty
 * boxes that all need the same weighbridge is arranged once and every container
 * generated from the order inherits it. A single box that needs something the
 * rest do not is handled on its own page, which is where divergence belongs.
 *
 * Each stop says where in the route it falls (`after`) rather than carrying a
 * position in one flat list. The order is a template and its form is built
 * around the three anchors; the container that is actually driven holds a flat
 * ordered array, and `buildStops` on the server is where the one becomes the
 * other.
 *
 * Held to less than an anchor. An anchor must resolve to a directory record —
 * a whole batch is dispatched to it and the record is where its coordinates and
 * contact live — while an added stop is routinely somewhere no directory holds.
 * What it does need is what dispatch actually depends on: a name, a street, a
 * city, and the map location that only a pick from the suggestions can produce.
 */
export function OrderAdditionalStops({
  fields,
  remove,
  register,
  setValue,
  watch,
  errors,
  readonly,
  getErrorMessage,
  countries,
  apiKey,
}: OrderAdditionalStopsProps) {
  const { t } = useTranslation(["containerOrders", "shipments"]);

  const stops = watch("route.additionalStops") ?? [];

  /** Which cards have had their address typed into since it was last picked. */
  const [typed, setTyped] = useState<Record<string, boolean>>({});
  const markTyped = useCallback(
    (id: string) => setTyped((prev) => ({ ...prev, [id]: true })),
    [],
  );

  /** The error sitting on one stop's field, if any. */
  const errorAt = (index: number, field: string) => {
    const stopErrors = (errors.route?.additionalStops as any)?.[index];
    return field
      .split(".")
      .reduce<any>((node, key) => node?.[key], stopErrors) as
      | { message?: string }
      | undefined;
  };

  /**
   * Typing in the address box means the text no longer describes the place that
   * was picked, so its coordinates are stale — drop them. The same rule the
   * three anchors follow, and the reason "pick it from the suggestions" is the
   * whole instruction: a location cannot be typed.
   */
  const handleTyped = (index: number, id: string, value: string) => {
    setValue(`route.additionalStops.${index}.address.street` as any, value);
    setValue(
      `route.additionalStops.${index}.address.coordinates` as any,
      undefined,
    );
    markTyped(id);
  };

  const applyPlace = (index: number, address: any) => {
    setValue(`route.additionalStops.${index}.address.street` as any, address.street, {
      shouldValidate: true,
    });
    if (address.city)
      setValue(`route.additionalStops.${index}.address.city` as any, address.city, {
        shouldValidate: true,
      });
    if (address.state)
      setValue(`route.additionalStops.${index}.address.state` as any, address.state);
    if (address.postalCode)
      setValue(
        `route.additionalStops.${index}.address.postalCode` as any,
        address.postalCode,
      );
    if (address.country)
      setValue(
        `route.additionalStops.${index}.address.country` as any,
        address.country,
      );
    setValue(
      `route.additionalStops.${index}.address.coordinates` as any,
      address.latitude != null && address.longitude != null
        ? { latitude: address.latitude, longitude: address.longitude }
        : undefined,
      { shouldValidate: true },
    );
  };

  return (
    <div className="mt-4 border-t border-[rgba(11,15,14,0.08)] pt-4">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="text-[12px] font-semibold text-ink">
          {t("route.additionalStops")}
        </span>
        <span className="text-[11px] text-mist">
          {t("route.additionalStopsHint")}
        </span>
      </div>

      {fields.length === 0 ? (
        <p className="text-[11px] text-mist">{t("route.noAdditionalStops")}</p>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {fields.map((field, index) => {
            const stop = stops[index] ?? {};
            const kind = (stop.kind ?? "other") as ContainerStopKind;
            const Icon = KIND_ICON[kind] ?? Signpost;
            const used = stop.enabled !== false;
            const coordinates = stop.address?.coordinates;
            const located =
              typeof coordinates?.latitude === "number" &&
              typeof coordinates?.longitude === "number";

            return (
              <div
                key={field.id}
                className={`@container rounded-md border p-3 ${
                  used
                    ? "border-[rgba(11,15,14,0.08)]"
                    : "border-dashed border-[rgba(11,15,14,0.18)] bg-bone/60"
                }`}
              >
                <div className="mb-2 flex items-center gap-2">
                  <span
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white ${
                      used ? "bg-amber-600" : "bg-secondary-300"
                    }`}
                  >
                    +
                  </span>
                  <Icon className="h-3.5 w-3.5 shrink-0 text-mist" />
                  <span
                    className={`truncate text-[12px] font-semibold ${
                      used ? "text-ink" : "text-mist line-through"
                    }`}
                  >
                    {stop.label?.trim() || t(`route.stopKinds.${kind}`)}
                  </span>
                  {!readonly && (
                    <button
                      type="button"
                      onClick={() => remove(index)}
                      title={t("route.removeStop")}
                      aria-label={t("route.removeStop")}
                      className="ml-auto rounded border border-[rgba(11,15,14,0.12)] p-1 text-mist hover:bg-signal/10 hover:text-signal"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </div>

                <div className="space-y-1">
                  {/* Where in the route it falls. First, because it is the one
                      field that decides what the stop *means* — the same
                      weighbridge before the loading site and after it are two
                      different instructions. */}
                  <FormField
                    layout="inline"
                    size="sm"
                    label={t("route.stopAfter")}
                    hint={t("route.stopAfterHint")}
                  >
                    <Select
                      {...register(`route.additionalStops.${index}.after`)}
                      size="sm"
                      disabled={readonly}
                      options={GAPS.map((gap) => ({
                        value: gap,
                        label: t(`route.gaps.${gap}`),
                      }))}
                    />
                  </FormField>

                  <FormField layout="inline" size="sm" label={t("route.stopKind")}>
                    <Select
                      {...register(`route.additionalStops.${index}.kind`)}
                      size="sm"
                      disabled={readonly}
                      options={CUSTOM_STOP_KIND_VALUES.map((value) => ({
                        value,
                        label: t(`route.stopKinds.${value}`),
                      }))}
                    />
                  </FormField>

                  <FormField
                    layout="inline"
                    size="sm"
                    label={t("route.stopLabel")}
                    hint={t("route.stopLabelHint")}
                  >
                    <Input
                      {...register(`route.additionalStops.${index}.label`)}
                      size="sm"
                      readOnly={readonly}
                      placeholder={t("route.stopLabelPlaceholder")}
                    />
                  </FormField>

                  <FormField
                    layout="inline"
                    size="sm"
                    required
                    label={t("route.stopName")}
                    hint={t("route.stopNameHint")}
                    error={getErrorMessage(errorAt(index, "companyName"))}
                  >
                    <Input
                      {...register(`route.additionalStops.${index}.companyName`)}
                      size="sm"
                      readOnly={readonly}
                      placeholder={t("placeholders.stopName")}
                      error={!!errorAt(index, "companyName")}
                    />
                  </FormField>

                  <FormField
                    layout="inline"
                    size="sm"
                    required
                    label={t("route.address")}
                    error={
                      getErrorMessage(errorAt(index, "address.street")) ??
                      getErrorMessage(errorAt(index, "address.city"))
                    }
                  >
                    <div className="space-y-1">
                      <div className="grid grid-cols-1 gap-2 @xs/control:grid-cols-3">
                        <div className="@xs/control:col-span-2">
                          <AddressAutocompleteField
                            apiKey={apiKey}
                            value={stop.address?.street || ""}
                            onChange={(value) =>
                              handleTyped(index, field.id, value)
                            }
                            onAddressSelect={(address) =>
                              applyPlace(index, address)
                            }
                            countries={countries}
                            disabled={readonly}
                            error={!!errorAt(index, "address.street")}
                            size="sm"
                          />
                        </div>
                        <Input
                          {...register(
                            `route.additionalStops.${index}.address.city`,
                          )}
                          size="sm"
                          readOnly={readonly}
                          placeholder={t("placeholders.city")}
                          error={!!errorAt(index, "address.city")}
                        />
                      </div>

                      {/* Said at every step rather than only on save — the same
                          statement the anchors make, because it is the same
                          requirement. A stop is a place a truck is sent to, and
                          "somewhere in Tallinn" is not one. */}
                      {!readonly &&
                        (located ? (
                          <p className="flex items-center gap-1 text-[11px] text-success-600">
                            <MapPin className="h-3 w-3 shrink-0" />
                            {t("route.locationCaptured", {
                              lat: coordinates!.latitude.toFixed(5),
                              lng: coordinates!.longitude.toFixed(5),
                            })}
                          </p>
                        ) : (
                          <p className="flex items-center gap-1 text-[11px] text-signal">
                            <AlertCircle className="h-3 w-3 shrink-0" />
                            {t(
                              typed[field.id]
                                ? "route.locationSelectFromSuggestions"
                                : "route.locationNotCaptured",
                            )}
                          </p>
                        ))}
                    </div>
                  </FormField>

                  <FormField
                    layout="inline"
                    size="sm"
                    label={t("route.stopDate")}
                    error={getErrorMessage(errorAt(index, "date"))}
                  >
                    <Input
                      {...register(`route.additionalStops.${index}.date`)}
                      type="date"
                      size="sm"
                      readOnly={readonly}
                      error={!!errorAt(index, "date")}
                    />
                  </FormField>

                  <FormField
                    layout="inline"
                    size="sm"
                    label={t("route.stopTime")}
                    error={getErrorMessage(errorAt(index, "time"))}
                  >
                    <Input
                      {...register(`route.additionalStops.${index}.time`)}
                      type="time"
                      size="sm"
                      readOnly={readonly}
                      error={!!errorAt(index, "time")}
                    />
                  </FormField>

                  <FormField layout="inline" size="sm" label={t("route.pin")}>
                    <Input
                      {...register(`route.additionalStops.${index}.pin`)}
                      size="sm"
                      readOnly={readonly}
                      className="font-mono placeholder:font-sans"
                      placeholder={t("placeholders.pin")}
                    />
                  </FormField>

                  {/* What to do here, in the dispatcher's own words. The only
                      field on the card that speaks to the driver in sentences,
                      and the one they read before getting out of the cab. */}
                  <FormField
                    layout="inline"
                    size="sm"
                    label={t("route.stopComment")}
                    hint={t("route.stopCommentHint")}
                  >
                    <Textarea
                      {...register(`route.additionalStops.${index}.comment`)}
                      size="sm"
                      rows={2}
                      readOnly={readonly}
                      placeholder={t("route.stopCommentPlaceholder")}
                    />
                  </FormField>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {fields.length >= MAX_ADDITIONAL && (
        <p className="mt-2 text-[11px] text-mist">
          {t("route.stopLimit", { max: MAX_ROUTE_STOPS })}
        </p>
      )}
    </div>
  );
}
